import { TeContainerSlim, TeForm, TeInputText, TeLabel } from '@topcon/tuicl-react';
import { ComponentProps, useEffect, useMemo, useRef, useState } from 'react';
import { TeChangeEvent, TeFormEvent } from '@topcon/tuicl';
import { z } from 'zod';

import { getFieldStatus, validateFieldValue } from './SchemaValidatedForm.utilities';

type ZodSchema = z.AnyZodObject;
type InferSchema<TSchema extends ZodSchema> = z.infer<TSchema>;
type KeyOfSchema<TSchema extends ZodSchema> = keyof InferSchema<TSchema>;

interface Props<TSchema extends ZodSchema> extends Omit<ComponentProps<typeof TeForm>, 'children'> {
  formData: InferSchema<TSchema>;
  setFormFields: (fields: Required<InferSchema<TSchema>>) => void;
  readonly?: boolean;
  children?: (args: {
    isValid: boolean;
    isTouched: boolean;
    renderField: (
      name: KeyOfSchema<TSchema>,
      placeholder: string,
      label?: string
    ) => React.ReactNode;
  }) => React.ReactNode;
  schema: TSchema;
  resetFieldsStatus?: boolean;
  touchAllOnReadonlyDisabled?: boolean;
}

function resetFormTouchFields<TSchema extends ZodSchema>(schema: TSchema) {
  return Object.keys(schema).reduce(
    (acc, key) => {
      acc[key as KeyOfSchema<TSchema>] = false;
      return acc;
    },
    {} as Record<KeyOfSchema<TSchema>, boolean>
  );
}

function touchAllFormFields<TSchema extends ZodSchema>(schema: TSchema) {
  return Object.keys(schema).reduce(
    (acc, key) => {
      acc[key as KeyOfSchema<TSchema>] = true;
      return acc;
    },
    {} as Record<KeyOfSchema<TSchema>, boolean>
  );
}

function SchemaValidatedForm<TSchema extends ZodSchema>({
  formData: formFields,
  setFormFields,
  readonly,
  children,
  schema,
  tesubmit,
  resetFieldsStatus,
  touchAllOnReadonlyDisabled,
  ...formProps
}: Props<TSchema>) {
  const defaultFormTouch = useMemo(() => resetFormTouchFields(schema), [schema]);
  const previousReadonly = useRef(readonly);

  const [formTouch, setFormTouch] = useState(defaultFormTouch);

  const handleTextInputChange = (e: TeChangeEvent, name: KeyOfSchema<TSchema>) => {
    const { target } = e;
    const { value } = target;
    const oldValue = formFields[name];
    if (value === oldValue) return;
    setFormFields({
      ...(formFields as Required<InferSchema<TSchema>>),
      [name]: value === '' ? undefined : value,
    });
    setFormTouch((prev) => (prev[name] ? prev : { ...prev, [name]: true }));
  };

  const isValid = useMemo(() => {
    for (const key of Object.keys(formFields)) {
      const { valid } = validateFieldValue(formFields[key], schema.shape[key]);
      if (!valid) return false;
    }
    return true;
  }, [formFields, schema]);

  const isTouched = useMemo(() => {
    return Object.values(formTouch).some((value) => value === true);
  }, [formTouch]);

  const handleSubmit = (e: TeFormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    tesubmit?.(e);
  };

  function renderField(name: KeyOfSchema<TSchema>, placeholder: string, label?: string) {
    const fieldValue = formFields[name];
    const { status, errorText } = getFieldStatus(fieldValue, formTouch[name], schema.shape[name]);

    const TextInput = (
      <TeInputText
        size="s"
        name={name as string}
        grow
        placeholder={placeholder}
        skin="light"
        value={fieldValue || ''}
        techange={(e) => handleTextInputChange(e, name)}
        status={readonly ? undefined : status}
        statusText={readonly ? undefined : errorText}
        readonly={readonly}
      />
    );
    if (!label) return TextInput;
    return (
      <TeContainerSlim column grow gap="xs">
        <TeLabel text={label} />
        {TextInput}
      </TeContainerSlim>
    );
  }

  useEffect(() => {
    if (resetFieldsStatus) {
      setFormTouch(resetFormTouchFields(schema));
    }
  }, [resetFieldsStatus, schema]);

  useEffect(() => {
    if (touchAllOnReadonlyDisabled && previousReadonly.current === true && readonly === false) {
      setFormTouch(touchAllFormFields(schema));
    }
    previousReadonly.current = readonly;
  }, [readonly, schema, touchAllOnReadonlyDisabled]);

  return (
    <TeForm tesubmit={handleSubmit} {...formProps}>
      {children?.({ isValid, isTouched, renderField })}
    </TeForm>
  );
}

export default SchemaValidatedForm;
